import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { DecisionChoice, DecisionStatsSnapshot } from '../game'
import { DecisionCard } from './DecisionCard'
import './DecisionDialog.css'

export interface DecisionDialogModel {
  eyebrow: string
  title: string
  description: string
  icon?: string
  stats?: DecisionStatsSnapshot
  choices: DecisionChoice[]
}

export function withPlayerStats(
  dialog: DecisionDialogModel,
  stats: DecisionStatsSnapshot,
): DecisionDialogModel {
  return dialog.stats ? dialog : { ...dialog, stats }
}

interface DecisionDialogProps {
  dialog: DecisionDialogModel | null
  onChoose: (choice: DecisionChoice, outcomeIndex?: number) => void | Promise<void>
}

const drawDelays = [85, 95, 110, 125, 145, 170, 205, 250, 315, 390] as const
type ResolutionPhase = 'idle' | 'committed' | 'drawing' | 'landing' | 'resolved'

function chanceOutcomes(choice: DecisionChoice) {
  return choice.effects.map((effect, index) => ({
    index,
    chance: effect.metric === 'condition' && effect.value.endsWith('%') ? Number.parseFloat(effect.value) : 0,
  })).filter((outcome) => Number.isFinite(outcome.chance) && outcome.chance > 0)
}

function rollOutcome(outcomes: ReturnType<typeof chanceOutcomes>): number {
  const total = outcomes.reduce((sum, outcome) => sum + outcome.chance, 0)
  let roll = Math.random() * total
  for (const outcome of outcomes) {
    roll -= outcome.chance
    if (roll < 0) return outcome.index
  }
  return outcomes.at(-1)?.index ?? 0
}

export function DecisionDialog({ dialog, onChoose }: DecisionDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const mountedRef = useRef(false)
  const timersRef = useRef(new Map<number, () => void>())
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const [highlightedEffectIndex, setHighlightedEffectIndex] = useState<number | null>(null)
  const [resolvedEffectIndex, setResolvedEffectIndex] = useState<number | null>(null)
  const [resolutionPhase, setResolutionPhase] = useState<ResolutionPhase>('idle')

  useEffect(() => {
    if (!dialog) return
    mountedRef.current = true
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus())

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), [tabindex]:not([tabindex="-1"])')]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', trapFocus)
    return () => {
      mountedRef.current = false
      timersRef.current.forEach((resolve, timer) => { window.clearTimeout(timer); resolve() })
      timersRef.current.clear()
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', trapFocus)
      openerRef.current?.focus()
      setSelectedId(null)
      setResolving(false)
      setHighlightedEffectIndex(null)
      setResolvedEffectIndex(null)
      setResolutionPhase('idle')
    }
  }, [dialog])

  if (!dialog) return null

  const pause = (duration: number) => new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => { timersRef.current.delete(timer); resolve() }, duration)
    timersRef.current.set(timer, resolve)
  })

  const choose = async (choice: DecisionChoice) => {
    if (resolving || choice.disabled) return
    if (resolvedEffectIndex !== null) {
      if (choice.id !== selectedId) return
      setResolving(true)
      try {
        await onChoose(choice, resolvedEffectIndex)
      } finally {
        if (mountedRef.current) setResolving(false)
      }
      return
    }
    setSelectedId(choice.id)
    setResolving(true)
    try {
      const outcomes = chanceOutcomes(choice)
      if (outcomes.length < 2) {
        await onChoose(choice)
        return
      }

      const winningIndex = rollOutcome(outcomes)
      const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (!reducedMotion) {
        setResolutionPhase('committed')
        await pause(260)
        if (!mountedRef.current) return
        setResolutionPhase('drawing')
        for (let step = 0; step < drawDelays.length; step += 1) {
          const isLastStep = step === drawDelays.length - 1
          setHighlightedEffectIndex(isLastStep ? winningIndex : outcomes[step % outcomes.length]!.index)
          await pause(drawDelays[step]!)
          if (!mountedRef.current) return
        }
        setResolutionPhase('landing')
        setHighlightedEffectIndex(winningIndex)
        await pause(520)
        if (!mountedRef.current) return
      }
      setHighlightedEffectIndex(winningIndex)
      setResolvedEffectIndex(winningIndex)
      setResolutionPhase('resolved')
      await pause(reducedMotion ? 80 : 420)
    } finally {
      if (mountedRef.current) setResolving(false)
    }
  }

  return (
    <div className="dialog-backdrop decision-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="decision-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className="decision-dialog__header">
          <span className="decision-dialog__icon" aria-hidden="true"><Icon icon={dialog.icon ?? 'mdi:strategy'} /></span>
          <div>
            <p className="eyebrow">{dialog.eyebrow}</p>
            <h2 id={titleId}>{dialog.title}</h2>
            <p id={descriptionId}>{dialog.description}</p>
          </div>
        </header>
        {dialog.stats && <dl className="decision-stats" aria-label="Estado actual del jugador">
          <div><dt>OVR</dt><dd>{dialog.stats.ovr}</dd></div>
          <div><dt>Físico</dt><dd>{dialog.stats.physical}/100</dd></div>
          <div><dt>Moral</dt><dd>{dialog.stats.morale}/100</dd></div>
          <div><dt>Prestigio</dt><dd>{dialog.stats.clubReputation}/100</dd></div>
        </dl>}
        <div className={`decision-grid decision-grid--${dialog.choices.length}${dialog.choices.length === 2 ? ' decision-grid--two' : ''}`}>
          {dialog.choices.map((choice) => (
            <DecisionCard
              key={choice.id}
              choice={choice}
              selected={choice.id === selectedId}
              resolving={resolving}
              awaitingConfirmation={resolvedEffectIndex !== null && choice.id === selectedId}
              locked={resolvedEffectIndex !== null && choice.id !== selectedId}
              highlightedEffectIndex={choice.id === selectedId ? highlightedEffectIndex : null}
              resolvedEffectIndex={choice.id === selectedId ? resolvedEffectIndex : null}
              resolutionPhase={choice.id === selectedId ? resolutionPhase : 'idle'}
              onChoose={choose}
            />
          ))}
        </div>
        <p className="dialog-note"><Icon icon="mdi:lock-outline" /> Esta decisión forma parte de la carrera y no se puede cerrar con Escape.</p>
      </section>
    </div>
  )
}
